// Undeclared dependencies on Aedu
import $ from 'sitewide-jquery';
import routes from 'sitewide-js/core_pre_webpack/routes';
import param from 'js/utils/param';

// SSR-safe: jQuery may not be available during server rendering
let csrfToken =
  typeof $ !== 'undefined' && $.rails && $.rails.csrfToken
    ? $.rails.csrfToken()
    : '__CROSS_SITE_REQUEST_FORGERY_PROTECTION_TOKEN__';

export function write(event_type, data, attempt = 0) {
  // SSR-safe: Don't attempt to send events during server rendering
  if (typeof window === 'undefined') {
    return;
  }

  if (
    csrfToken === '__CROSS_SITE_REQUEST_FORGERY_PROTECTION_TOKEN__' &&
    (attempt || 0) < 5
  ) {
    setTimeout(() => {
      csrfToken = $.rails.csrfToken();
      write(event_type, data, (attempt || 0) + 1);
    }, 1000);
    return;
  }
  const hit_id = (window.Aedu && Aedu.hit_data && Aedu.hit_data.id) || null;
  const body = JSON.stringify({ event_type, data, hit_id });

  const url = routes.api_v0_arbitrary_event_index_path({
    subdomain_param: 'api',
  });
  const sendBeaconUrl = `${url}&${param({ body })}`;

  // We attempt to send this data in the most async friendly way possible.
  // In order, these are the following:
  //
  // 1. fetch(url, {keepalive: true})
  //    The modern replacement for navigator.sendBeacon(). Creates a request
  //    that outlives the page, with all the power of fetch() behind it.
  //    As of September 2019, only supported by Chrome.
  //
  // 2. navigator.sendBeacon(url)
  //    The old way to create a request that outlives the page. Can't send
  //    json, so we must use a query parameter. Can't send arbitrary headers.
  //    Limited to 8 KB of data by Nginx's large_client_header_buffers param.
  //    Supported by all modern browsers but not IE.
  //
  // 3. fetch(url)
  //    A synchronous request. Will be canceled within a beforeunload handler.
  //    Polyfilled by us.
  const useFetch =
    (typeof Request !== 'undefined' &&
      'keepalive' in new Request('https://www.example.com')) ||
    typeof navigator === 'undefined' ||
    !navigator.sendBeacon ||
    sendBeaconUrl.length > 8000;

  if (useFetch) {
    return fetch(url, {
      method: 'POST',
      body: body,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },

      // Allows the request to outlive the page.
      // Ignored by browsers that don't support it.
      keepalive: true,

      // Must be explicitly set when using keepalive
      mode: 'same-origin',
    }).then((r) => {
      if (r.ok) {
        return r.json();
      } else {
        const error = new Error(
          `arbitrary_events.write failed: ${r.status} ${r.statusText}`,
        );
        error.name = 'ArbitraryEventError';
        return Promise.reject(error);
      }
    });
  } else {
    navigator.sendBeacon(sendBeaconUrl);
  }
}

export function bulkWrite(event_type, dataArray, attempt = 0) {
  // SSR-safe: Don't attempt to send events during server rendering
  if (typeof window === 'undefined') {
    return;
  }

  if (
    csrfToken === '__CROSS_SITE_REQUEST_FORGERY_PROTECTION_TOKEN__' &&
    (attempt || 0) < 5
  ) {
    setTimeout(() => {
      csrfToken = $.rails.csrfToken();
      bulkWrite(event_type, dataArray, (attempt || 0) + 1);
    }, 1000);
    return;
  }

  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return Promise.reject(new Error('dataArray must be a non-empty array'));
  }

  const hit_id = (window.Aedu && Aedu.hit_data && Aedu.hit_data.id) || null;
  const body = JSON.stringify({ event_type, data_array: dataArray, hit_id });

  const url = routes.bulk_create_api_v0_arbitrary_event_index_path({
    subdomain_param: 'api',
  });
  const sendBeaconUrl = `${url}&${param({ body })}`;

  // For bulk writes, we prefer fetch over sendBeacon due to potentially larger payload sizes
  // and the need for better error handling with multiple events
  const useFetch =
    (typeof Request !== 'undefined' &&
      'keepalive' in new Request('https://www.example.com')) ||
    typeof navigator === 'undefined' ||
    !navigator.sendBeacon ||
    sendBeaconUrl.length > 8000;

  if (useFetch) {
    return fetch(url, {
      method: 'POST',
      body: body,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },

      // Allows the request to outlive the page.
      // Ignored by browsers that don't support it.
      keepalive: true,

      // Must be explicitly set when using keepalive
      mode: 'same-origin',
    }).then((r) => {
      if (r.ok) {
        return r.json();
      } else {
        const error = new Error(
          `arbitrary_events.bulkWrite failed: ${r.status} ${r.statusText}`,
        );
        error.name = 'ArbitraryEventError';
        return Promise.reject(error);
      }
    });
  } else {
    navigator.sendBeacon(sendBeaconUrl);
  }
}
